import React, { useCallback, useState } from "react";
import { aiApi } from "../../api/endpoints";
import { FactChip } from "../chips/FactChip";
import { SeverityChip } from "../chips/SeverityChip";
import { AIDialog } from "./AIDialog";
import { AIMandatedLabel } from "./AIMandatedLabel";

/**
 * ValidationSuggestionDialog — surfaces the deterministic validation
 * recommendation for a finding and (optionally) an AI-polished
 * rephrasing. The deterministic version is ALWAYS shown prominently
 * and never replaced by the polish.
 *
 * Flow:
 *   1. On open (auto-fires) → POST /findings/{fid}/validation
 *      (deterministic-only). Renders immediately with a CALCULATED
 *      chip. Fast; no LLM invocation.
 *   2. User clicks "Rephrase with AI" → same endpoint with ?polish=true.
 *      Renders the AI phrasing below the deterministic version with
 *      INFERRED chip + mandated label.
 *   3. If the polish is rejected by the guardrail, an inline notice
 *      appears; the deterministic version remains the only rendered
 *      recommendation.
 */
export function ValidationSuggestionDialog({ workspaceId, runId, finding }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [polishing, setPolishing] = useState(false);

  const fetchDeterministic = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await aiApi.validationRecommendation(
        workspaceId, runId, finding.id, { polish: false },
      );
      setPayload(res);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, runId, finding.id]);

  const requestPolish = useCallback(async () => {
    setPolishing(true);
    setError(null);
    try {
      const res = await aiApi.validationRecommendation(
        workspaceId, runId, finding.id, { polish: true },
      );
      setPayload(res);
    } catch (e) {
      setError(e);
    } finally {
      setPolishing(false);
    }
  }, [workspaceId, runId, finding.id]);

  const testIdPrefix = `validation-${finding.id}`;

  return (
    <AIDialog
      testIdPrefix={testIdPrefix}
      ariaLabel={`Validation recommendation for finding ${finding.rule_id}`}
      onOpenChange={(next) => {
        if (next) {
          // Refresh every open so `cached` reflects backend truth
          // (mirrors ExplainFindingDialog behaviour).
          fetchDeterministic();
        }
      }}
      renderTrigger={({ triggerRef, onClick, onKeyDown, ariaExpanded }) => (
        <button
          ref={triggerRef}
          type="button"
          onClick={onClick}
          onKeyDown={onKeyDown}
          aria-haspopup="dialog"
          aria-expanded={ariaExpanded}
          data-testid={`validation-trigger-${finding.id}`}
          className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          <span aria-hidden="true" className="text-emerald-600">◈</span>
          Suggest validation
        </button>
      )}
      renderHeader={() => (
        <div className="min-w-0">
          <AIMandatedLabel testId={`validation-mandated-label-${finding.id}`} />
          <h2 className="mt-0.5 truncate text-sm font-medium text-slate-900">
            Validation for {finding.rule_id}
          </h2>
          <div className="mt-1 flex items-center gap-2">
            <SeverityChip severity={finding.effective_severity} />
            <FactChip source="CALCULATED" />
            {payload?.cached && (
              <span
                data-testid={`validation-cached-${finding.id}`}
                className="text-[10px] uppercase tracking-wider text-slate-500"
              >
                cached
              </span>
            )}
          </div>
        </div>
      )}
      renderBody={() => (
        <div className="space-y-4">
          {loading && (
            <div
              data-testid={`validation-loading-${finding.id}`}
              className="text-sm text-slate-500"
            >
              Loading recommendation…
            </div>
          )}
          {error && (
            <div
              data-testid={`validation-error-${finding.id}`}
              className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800"
            >
              {error?.body?.error?.message || "Something went wrong."}
            </div>
          )}
          {payload && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                  Deterministic recommendation
                </div>
                <p
                  data-testid={`validation-deterministic-${finding.id}`}
                  className="mt-1 text-sm text-slate-900"
                >
                  {payload.deterministic_recommendation}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <FactChip source="CALCULATED" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    Category: {payload.recommendation_category}
                  </span>
                </div>
              </div>

              {/* AI polish action + result */}
              {!payload.ai_phrasing && !polishing && (
                <div className="border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    onClick={requestPolish}
                    data-testid={`validation-polish-button-${finding.id}`}
                    className="inline-flex items-center gap-1 rounded border border-violet-300 bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  >
                    <span aria-hidden="true" className="text-violet-600">✦</span>
                    Rephrase with AI
                  </button>
                  {payload.guardrail_status === "rejected_fallback" && (
                    <div
                      data-testid={`validation-rejected-banner-${finding.id}`}
                      className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                    >
                      The AI rephrasing was rejected by the guardrail
                      (<code>{payload.reason_code}</code>). The deterministic
                      recommendation above is unchanged.
                    </div>
                  )}
                </div>
              )}

              {polishing && (
                <div
                  data-testid={`validation-polishing-${finding.id}`}
                  className="text-sm text-slate-500"
                >
                  Rephrasing with the AI reviewer…
                </div>
              )}

              {payload.ai_phrasing && (
                <div
                  data-testid={`validation-ai-phrasing-wrap-${finding.id}`}
                  className="border-t border-slate-100 pt-3"
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    AI-rephrased version
                  </div>
                  <p
                    data-testid={`validation-ai-phrasing-${finding.id}`}
                    className="mt-1 text-sm text-slate-900"
                  >
                    {payload.ai_phrasing}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <FactChip source="INFERRED" />
                    {payload.cached && (
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">
                        cached
                      </span>
                    )}
                  </div>
                </div>
              )}

              {payload.guardrail_status === "not_invoked" && payload.ai_error && (
                <div
                  data-testid={`validation-ai-error-${finding.id}`}
                  className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
                >
                  AI polish unavailable ({payload.ai_error.code}). Deterministic
                  recommendation above is authoritative.
                </div>
              )}
            </>
          )}
        </div>
      )}
      renderFooter={() =>
        payload?.model ? (
          <>
            AI model: <code>{payload.model}</code>
            {" · "}Prompt: <code>v{payload.prompt_version}</code>
          </>
        ) : (
          <>Deterministic — no AI invocation.</>
        )
      }
    />
  );
}
